import { useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Shown once on first login/signup. Blocks the app until acknowledged.
 * Persists via /api/auth/acknowledge → user.disclaimer_signup_ack_at.
 */
export default function SignupDisclaimer() {
  const { user, refresh } = useAuth();
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  if (!user || user.disclaimer_signup_ack_at) return null;

  const accept = async () => {
    if (!confirmed) return toast.error("Please tick the box to continue");
    setSaving(true);
    try {
      await api.post("/auth/acknowledge", { scope: "signup" });
      await refresh?.();
      toast.success("Welcome — you're all set ♡");
    } catch {
      toast.error("Couldn't save. Please try again.");
    } finally { setSaving(false); }
  };

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[60] bg-kawaii-text/40 backdrop-blur-md flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      data-testid="signup-disclaimer"
    >
      <motion.div
        initial={{ scale: 0.94, y: 10 }} animate={{ scale: 1, y: 0 }}
        className="kawaii-card max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 md:p-8"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-2xl bg-[#FFF5F5] border border-[#E63946]/30 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-[#E63946]" strokeWidth={2.5} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-kawaii-textSoft font-bold">Please read before continuing</p>
            <h2 className="font-heading text-2xl text-kawaii-text mt-1">Important — this is not medical advice</h2>
          </div>
        </div>

        <div className="space-y-4 text-sm font-body text-kawaii-text leading-relaxed">
          <div className="rounded-2xl bg-gradient-to-r from-kawaii-lavender/20 via-kawaii-pink/15 to-kawaii-peach/20 border border-kawaii-lavender/40 p-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-kawaii-lavender mb-1 flex items-center gap-1">
              <Check className="w-3 h-3" strokeWidth={2.5} /> Your 7-day Pro trial is already active
            </p>
            <p className="text-sm">
              Unlimited Lymphie chat, unlimited photos, PDF exports, and full Emergency access — for free, no card needed. Explore everything ♡
            </p>
          </div>
          <p>
            <strong className="font-semibold">This app is for tracking purposes only and does not provide medical advice.</strong>{" "}
            Always seek the advice of a physician for any medical emergency or treatment.
          </p>
          <div className="rounded-2xl bg-[#FFF5F5] border border-[#E63946]/25 p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#E63946] mb-1">Emergency feature</p>
            <p className="text-sm">
              The Emergency script and ER timeline are assistants only — they are <strong>not</strong> a substitute for professional emergency care.
              We are not liable for any outcome arising from their use. In a real emergency, call your local emergency number first.
            </p>
          </div>
          <div className="rounded-2xl bg-white border border-kawaii-border p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-kawaii-textSoft mb-1">Your data</p>
            <ul className="space-y-1.5 list-disc pl-4">
              <li>Stored in a private database created specifically for this app on deployment.</li>
              <li>We do not sell your personal information to third parties.</li>
              <li>Your data belongs to you — export or delete it any time.</li>
            </ul>
          </div>
          <p className="text-xs text-kawaii-textSoft italic">
            Read the full{" "}
            <Link to="/terms" className="underline underline-offset-2 text-kawaii-text">Terms of Service</Link>
            {" "}and{" "}
            <Link to="/privacy" className="underline underline-offset-2 text-kawaii-text">Privacy Policy</Link>.
          </p>
        </div>

        <label className="mt-5 flex items-center gap-3 p-3 rounded-2xl bg-kawaii-lavender/15 border border-kawaii-lavender/40 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            data-testid="disclaimer-confirm-cb"
            className="w-4 h-4 accent-kawaii-lavender"
          />
          <span className="text-sm font-body text-kawaii-text">
            I understand this app is not a substitute for medical advice and accept the Terms.
          </span>
        </label>

        <div className="flex justify-end mt-5">
          <button
            onClick={accept}
            disabled={!confirmed || saving}
            data-testid="disclaimer-accept-btn"
            className="kawaii-btn px-6 py-2.5 rounded-full bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white font-heading font-semibold text-sm shadow-md inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={2.5} />}
            I understand — continue
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}
